import { useState } from 'react'
import AuditOutcomeList, { type AuditOutcomeItem } from './AuditOutcomeList'

export interface AuditOutcomeBucket {
  count: number
  items: AuditOutcomeItem[]
}

// Payload do DigitalWorkers::AuditOutcomes (prop deferida `audit_outcomes`).
export interface AuditOutcomesData {
  window_days: number
  generated_at: string
  degraded_sources: string[]
  buckets: {
    resolvidas: AuditOutcomeBucket
    pendentes: AuditOutcomeBucket
    atencao: AuditOutcomeBucket
    escaladas: AuditOutcomeBucket
  }
}

type BucketKey = keyof AuditOutcomesData['buckets']

const BUCKET_TABS: { key: BucketKey; label: string; empty: string }[] = [
  {
    key: 'resolvidas',
    label: 'Resolvidas',
    empty: 'Nenhuma conversa resolvida no período ainda.',
  },
  {
    key: 'pendentes',
    label: 'Pendentes',
    empty: 'Nada em andamento agora.',
  },
  {
    key: 'atencao',
    label: 'Atenção',
    empty: 'Sem alertas nem reprovações no período. Tudo em ordem.',
  },
  {
    key: 'escaladas',
    label: 'Escaladas',
    // Zero-state honesto: o único sinal de escalada capturado hoje vem da
    // avaliação automática (needed_human); não há registro de transferência
    // explícita para atendimento humano.
    empty:
      'Nenhuma escalada no período. Hoje o sinal vem da avaliação automática das sessões ' +
      '(tarefa que precisou de intervenção humana); a transferência explícita para um humano ainda não é capturada.',
  },
]

const SOURCE_LABELS: Record<string, string> = {
  fleet_sessions: 'telemetria de sessões',
  evals: 'avaliações',
  alerts: 'alertas',
  chats: 'conversas do chat',
}

interface Props {
  data: AuditOutcomesData | null
}

export default function AuditOutcomes({ data }: Props) {
  const [activeBucket, setActiveBucket] = useState<BucketKey>('resolvidas')

  if (!data) {
    // Estado vazio do DS: uma frase só, média — sem caixa.
    return <p className="py-4 text-[13px] text-muted-foreground">Não foi possível carregar os desfechos das conversas.</p>
  }

  const bucket = data.buckets[activeBucket]
  const activeTab = BUCKET_TABS.find((tab) => tab.key === activeBucket) ?? BUCKET_TABS[0]

  return (
    <div className="bg-card rounded-xl border border-border">
      <div className="px-4 pt-4">
        <h3 className="text-[14px] font-semibold text-foreground">Conversas por desfecho</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Desfechos derivados da telemetria, das avaliações e dos alertas · últimos {data.window_days} dias
        </p>
        {data.degraded_sources.length > 0 && (
          <p className="mt-2 text-xs text-warning">
            Fontes indisponíveis agora: {data.degraded_sources.map((s) => SOURCE_LABELS[s] ?? s).join(', ')}. Os
            desfechos abaixo podem estar incompletos.
          </p>
        )}
        <div className="mt-3 flex gap-1 bg-accent rounded-lg p-1 overflow-x-auto" role="tablist" aria-label="Desfechos">
          {BUCKET_TABS.map(({ key, label }) => {
            const count = data.buckets[key].count
            const attention = (key === 'atencao' || key === 'escaladas') && count > 0
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeBucket === key}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  activeBucket === key
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-accent-foreground'
                }`}
                onClick={() => setActiveBucket(key)}
              >
                {label}
                <span className={`ml-1.5 tabular-nums text-xs ${attention ? 'font-semibold text-destructive' : ''}`}>
                  {count}
                </span>
              </button>
            )
          })}
        </div>
      </div>
      <div className="mt-3 border-t border-border">
        <AuditOutcomeList items={bucket.items} count={bucket.count} emptyMessage={activeTab.empty} />
      </div>
    </div>
  )
}
